import { createContext, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useAuth } from "@/hooks/useAuth";
import { ensureProfile } from "@/features/auth/ensureProfile";
import { friendlyDataError } from "@/lib/errors";

export const ProfileContext = createContext(null);
const IDLE = { status: "idle", profile: null, error: null };

export function ProfileProvider({ children }) {
  const { user } = useAuth();
  const userId = user?.id;
  const userRef = useRef(user);
  userRef.current = user;
  const [state, setState] = useState(IDLE);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    if (!userId) {
      setState(IDLE);
      return;
    }
    let cancelled = false;
    setState((s) => ({ status: "loading", profile: s.profile?.user_id === userId ? s.profile : null, error: null }));
    ensureProfile(userRef.current).then(
      (profile) => !cancelled && setState({ status: "ready", profile, error: null }),
      (err) =>
        !cancelled &&
        setState({ status: "error", profile: null, error: friendlyDataError(err, "We couldn't load your profile.") }),
    );
    return () => {
      cancelled = true;
    };
  }, [userId, attempt]);

  const retry = useCallback(() => setAttempt((a) => a + 1), []);
  const setProfile = useCallback((profile) => setState({ status: "ready", profile, error: null }), []);

  const value = useMemo(() => {
    const loading = Boolean(userId) && (state.status === "idle" || state.status === "loading") && !state.profile;
    return { ...state, loading, retry, setProfile };
  }, [state, userId, retry, setProfile]);

  return <ProfileContext.Provider value={value}>{children}</ProfileContext.Provider>;
}
